import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';

import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';

import {
  PATIENT_INQUIRY_PER_PAGE_OPTIONS,
  usePatientInquiryListStore,
} from '../store/patient-inquiry-list.store';

// PRD §3.2: search is debounced 300 ms; Enter / icon-click trigger immediately.
const SEARCH_DEBOUNCE_MS = 300;

export function PatientInquiryListFilters() {
  const { t } = useTranslation('admin');
  const search = usePatientInquiryListStore((s) => s.filters.search);
  const perPage = usePatientInquiryListStore((s) => s.perPage);
  const setSearch = usePatientInquiryListStore((s) => s.setSearch);
  const setPerPage = usePatientInquiryListStore((s) => s.setPerPage);

  // Local input drives instant typing feedback; the store (and the list query)
  // updates only after the trailing debounce. Enter / icon click flush early.
  const [searchLocal, setSearchLocal] = useState(search);
  const debouncedSearch = useDebouncedValue(searchLocal, SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    setSearchLocal(search);
  }, [search]);

  useEffect(() => {
    if (debouncedSearch !== search) {
      setSearch(debouncedSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const flushSearch = () => {
    if (searchLocal !== search) setSearch(searchLocal);
  };

  return (
    <div className="filter-bar">
      <div className="filter-bar__search" style={{ flex: 1 }}>
        <input
          type="text"
          className="filter-bar__search-input"
          placeholder={t('patientInquiry.search.placeholder')}
          value={searchLocal}
          onChange={(e) => setSearchLocal(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') flushSearch();
          }}
        />
        <button
          type="button"
          onClick={flushSearch}
          className="filter-bar__search-icon-button"
          aria-label={t('patientInquiry.search.button')}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            color: 'var(--muted-foreground)',
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          <Search className="filter-bar__search-icon" size={16} />
        </button>
      </div>

      <select
        className="filter-bar__select"
        style={{ width: 140, marginLeft: 'auto' }}
        value={perPage}
        onChange={(e) => setPerPage(Number(e.target.value))}
      >
        {PATIENT_INQUIRY_PER_PAGE_OPTIONS.map((n) => (
          <option key={n} value={n}>
            {t('patientInquiry.perPage.label', { count: n })}
          </option>
        ))}
      </select>
    </div>
  );
}
